import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, RefreshCw, Rocket } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import KiArbeitskarte from "@/components/landing/KiArbeitskarte";
import ChrisLp3Waiting from "@/components/landing/chris/ChrisLp3Waiting";
import WeltDokument from "@/components/landing/WeltDokument";
import WeltVorschlaege from "@/components/landing/WeltVorschlaege";
import ConversionBlock from "@/components/landing/ConversionBlock";
import EinmalkaufPanel from "@/components/landing/EinmalkaufPanel";
import MitarbeiterZuweisungDemo from "@/components/landing/MitarbeiterZuweisungDemo";
import PricingSection from "@/components/landing/PricingSection";
import { erfasseHerkunft, mitHerkunft } from "@/lib/herkunft";
import {
  generierungsbegriff, gemerkteVorschau, merkeErgebnis, merkeVorschau, vergissVorschau,
  themaAusSuchbegriff, // ADS-SUCHBEGRIFF-01, Betreiberfreigabe 01.11.2026
} from "@/lib/generierungsbegriff"; // ADS-AUTOPREVIEW 01 Schritt 3, Betreiberfreigabe 30.10.2026
import BegriffAuswahl, { ThemaFrage } from "@/components/landing/BegriffAuswahl"; // ADS-SUCHBEGRIFF-01, Betreiberfreigabe 01.11.2026
import { meldeStufe } from "@/lib/messStufe"; // MESS-10d, Betreiberfreigabe 03.11.2026
import "@/styles/landing.css";

// LP4 – Öffentliche Vorführung mit echter, read-only KI-Vorschau.
// Ablauf: Eingabe → Vollbild-Ladezustand → KOMPLETTE Welt (ein Dokument, keine
// Tabs) → „Mitarbeiter zuweisen" → Abschlussansicht. KEINE Speicherung/Auth.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// PLAN1.2 – Feature-Flag (Default aus). Nur wenn explizit "true", wird der
// zusätzliche, leichte Vorschlags-Call abgesetzt. Aus = exakt der alte Flow.
const SUGGESTIONS_ENABLED =
  process.env.REACT_APP_LANDING_SUGGESTIONS_ENABLED === "true";
const MAX_PREVIEWS = 100;
const LS_KEY = "clartext_lp_previews";
const DAY_MS = 24 * 60 * 60 * 1000;
const STEP_INTERVAL_MS = 1700;
const POLL_MS = 2500;

// BLOCK3A · KI-Erlebnis: Zwei nacheinander laufende Animationsabschnitte
// mit einzeln aktivierten Haken. Der Loader-Titel ist in beiden Abschnitten
// identisch ("CLartext bereitet Ihre Dokumentation vor …").
//
// Abschnitt A · sieben feste, eingabeneutrale Zeilen. Die tatsächliche
// Eingabe erscheint separat im Stage-Term ("Vorschau für „{term}"").
// KEINE Fantasieinformationen (Betrieb, Branche, Zahlen, Normen, PSA).
const STATUS_STEPS = [
  "Tätigkeit verstanden",
  "Gefährdungen werden ermittelt",
  "Unterweisung wird erstellt",
  "Betriebsanweisung wird vorbereitet",
  "Prüffristen werden ermittelt",
  "Nachweise werden vorbereitet",
  "Dokumentation wird fertiggestellt",
];

// Abschnitt B · fünf eingabebezogene Zeilen. Die ersten drei Zeilen tragen die
// tatsächliche Nutzereingabe (kein Betrieb/Branche/Standort ergänzen); die
// letzten beiden sind bewusst generisch und werden nicht mit Eingabe-Text
// erweitert.
function buildStepsB(term) {
  const t = (term || "").trim();
  return [
    `Gefährdungsbeurteilung: ${t}`,
    `Unterweisung: ${t}`,
    `Betriebsanweisung: ${t}`,
    "Prüffristen und Wiederholungen werden vorbereitet",
    "Nachweise und Prüfmappe werden vorbereitet",
  ];
}

const DEMO_STEPS = [
  "Demo-Betrieb wird angelegt",
  "Einsatzbereiche werden vorbereitet",
  "Mitarbeiter werden angelegt",
  "Dokumentation wird freigegeben",
  "Aufgaben werden erstellt",
  "Prüfmappe wird vorbereitet",
  "Ihre Demo ist bereit",
];
const DEMO_LAST_KEY = "clartext_lp_demo_at";

function recentPreviews() {
  try {
    const cutoff = Date.now() - DAY_MS;
    return (JSON.parse(localStorage.getItem(LS_KEY) || "[]")).filter((t) => t > cutoff);
  } catch {
    return [];
  }
}
function recordPreview() {
  const arr = recentPreviews();
  arr.push(Date.now());
  try { localStorage.setItem(LS_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
  return arr.length;
}

export default function LandingPage() {
  // phase: "search" | "loading" | "error" | "limit" | "reveal" | "world" | "assign" | "done"
  // BLOCK3A · "loading" enthält zwei nacheinander ablaufende Animationsabschnitte
  // (loaderStage="A" mit 7 Schritten, dann loaderStage="B" mit 5 Schritten).
  // "reveal" spielt die Abschluss-Dramaturgie ab (Karte fade-out, Pause,
  // Headline/Subline fade-in, 4.000-ms-Halt, fade-out) und wechselt danach
  // in "world" (WeltDokument). Das EinmalkaufPanel erscheint verzögert erst
  // nach 2.000 ms in "world" (nicht während des Abschlussscreens).
  const [phase, setPhase] = useState("search");
  const [term, setTerm] = useState("");
  const [world, setWorld] = useState(null);
  const [doneCount, setDoneCount] = useState(0);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [, setPreviewsUsed] = useState(() => recentPreviews().length);
  // BLOCK3A · Zwei-Abschnitts-Loading. "A" = allgemein (7 Zeilen · STATUS_STEPS),
  // "B" = eingabebezogen (5 Zeilen · buildStepsB(term)). Wechsel erfolgt, sobald
  // in Abschnitt A alle sieben Haken einzeln gesetzt wurden.
  const [loaderStage, setLoaderStage] = useState("A");
  // BLOCK3A · Signal des Backends: Vorschau steht bereit. Der Übergang zu
  // "reveal" erfolgt erst, wenn BEIDE Bedingungen erfüllt sind: alle Haken
  // gesetzt UND Backend-Ergebnis vorhanden. So werden nie alle Haken auf
  // einmal aktiviert und nie ein hartes Ergebnis-Popup ausgelöst.
  const [resultReady, setResultReady] = useState(false);
  // BLOCK3A · Sub-Phase innerhalb "reveal": hold-card → fade-card → pause →
  // content-in → hold → fade-out. Steuert die exakt festgelegten Übergänge.
  const [revealPhase, setRevealPhase] = useState("hold-card");
  // BLOCK3 · Kaufdialog erst verzögert einblenden, damit der Abschlussmoment
  // (WeltDokument + Reveal) nicht kommerziell überschrieben wird.
  const [showEinmalkauf, setShowEinmalkauf] = useState(false);
  // PLAN1.2 – Vorschläge (nur wenn Feature-Flag an). Additiv, blockiert nie.
  const [suggestions, setSuggestions] = useState([]);
  const [suggestMode, setSuggestMode] = useState("single");
  const [suggestReady, setSuggestReady] = useState(false);
  const [standardwelten, setStandardwelten] = useState([]); // ADS-AUTOPREVIEW 01 Stufe 4.4B/4.5, Betreiberfreigabe 31.10.2026

  const cache = useRef({});
  const suggCache = useRef({});
  const stepTimer = useRef(null);
  const pollTimer = useRef(null);
  const revealTimer = useRef(null);
  const einmalkaufTimer = useRef(null);
  const genId = useRef(0);
  const begriffRef = useRef(""); // ADS-AUTOPREVIEW 01 Schritt 3: Begriff aus dem Ziel-Link (Schlüssel der Wiederaufnahme)
  const loadingStepsRef = useRef(STATUS_STEPS);

  // BLOCK3A · Aktuelle Schrittliste des Loaders — abhängig vom loaderStage.
  // Kein Umschalten aus Suggestions, kein Erfinden neuer Zeilen.
  const loadingSteps = useMemo(
    () => (loaderStage === "A" ? STATUS_STEPS : buildStepsB(term)),
    [loaderStage, term],
  );
  loadingStepsRef.current = loadingSteps;

  const clearTimers = () => {
    if (stepTimer.current) { clearInterval(stepTimer.current); stepTimer.current = null; }
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    if (revealTimer.current) { clearTimeout(revealTimer.current); revealTimer.current = null; }
    if (einmalkaufTimer.current) { clearTimeout(einmalkaufTimer.current); einmalkaufTimer.current = null; }
  };
  useEffect(() => clearTimers, []);

  // CHRIS · LP3 WAITING EXPERIENCE v1 (2026-09-27): Die frühere Loader-Staffelung
  // (STATUS_STEPS/buildStepsB) und die Reveal-Dramaturgie wurden durch die
  // Chris-geführte Warteerfahrung `<ChrisLp3Waiting>` ersetzt (siehe <main>).
  // Der Backend-Vertrag bleibt UNVERÄNDERT: `generate()` startet die Vorschau,
  // `poll()`/`finishGeneration()` setzen `world` + `resultReady`. Der Übergang in
  // den bestehenden Abo-Workflow erfolgt AUSSCHLIESSLICH EINMAL über
  // `handoffToWelt()`, sobald Chris seine Abschluss-Sequenz beendet — keine
  // zweite Redirect-Logik, kein Race (Auftrag §8). Die frühere Reveal-Dramaturgie
  // (hold-card/fade-card/…/redirect) ist damit obsolet und entfernt.
  const handoffToWelt = () => {
    // Identischer Vertrag wie zuvor (BLOCK 3D · R3): Preview-Session in
    // localStorage schreiben und Full-Page-Redirect in den Abo-Workflow.
    const token = world?.preview_session_token;
    const worldId = world?.world_id;
    if (!token || !worldId) {
      failGeneration("Die Vorschau konnte nicht geöffnet werden. Bitte erneut versuchen.");
      return;
    }
    try {
      localStorage.setItem(
        "owner_session",
        JSON.stringify({ preview_token: token, tenant_id: "public-preview" }),
      );
    } catch { /* localStorage optional */ }
    meldeStufe("vorschau_angezeigt", worldId); // MESS-10d, Betreiberfreigabe 03.11.2026
    window.location.href = `/unternehmer/welt/${worldId}`;
  };

  // BLOCK3 · Einmalkauf-Panel erscheint erst nach dem Abschlussmoment. In den
  // Nicht-Ergebnis-Phasen bleibt es unsichtbar; in "world" wird es exakt nach
  // 2000 ms eingeblendet (WeltDokument atmet zuerst ohne Kauf-CTA).
  useEffect(() => {
    if (phase !== "world") {
      setShowEinmalkauf(false);
      if (einmalkaufTimer.current) { clearTimeout(einmalkaufTimer.current); einmalkaufTimer.current = null; }
      return;
    }
    einmalkaufTimer.current = setTimeout(() => {
      einmalkaufTimer.current = null;
      setShowEinmalkauf(true);
    }, 2000);
    return () => {
      if (einmalkaufTimer.current) { clearTimeout(einmalkaufTimer.current); einmalkaufTimer.current = null; }
    };
  }, [phase]);
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const goTest = () => { window.location.href = "/unternehmer/login"; };

  // D1.1 – Self-Service-Demo: erzeugt im Hintergrund einen frischen Demo-Mandanten
  // (nur Muster-Mitarbeiter, setup_completed=false), loggt den Besucher automatisch
  // ein (kein Code, keine PIN) und leitet nach /unternehmer → dort öffnet sich
  // wegen setup_completed=false automatisch der SetupWizard.
  const startDemo = async () => {
    // Clientseitiges Limit: 1 Demo pro Browser / 24 h.
    try {
      const last = Number(localStorage.getItem(DEMO_LAST_KEY) || 0);
      if (last && Date.now() - last < DAY_MS) {
        setError(null);
        setPhase("demo-limit");
        scrollTop();
        return;
      }
    } catch { /* localStorage optional */ }

    const myGen = ++genId.current;
    clearTimers();
    setError(null);
    setDoneCount(0);
    setChecking(false);
    setPhase("demo");
    scrollTop();

    stepTimer.current = setInterval(() => {
      setDoneCount((prev) => {
        if (prev >= DEMO_STEPS.length - 1) {
          if (stepTimer.current) { clearInterval(stepTimer.current); stepTimer.current = null; }
          setChecking(true);
          return prev;
        }
        return prev + 1;
      });
    }, STEP_INTERVAL_MS);

    try {
      const res = await fetch(`${API}/public/demo-start-wizard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(mitHerkunft({})),
      });
      if (myGen !== genId.current) return;
      if (!res.ok) {
        clearTimers();
        if (res.status === 429) { setPhase("demo-limit"); scrollTop(); return; }
        const j = await res.json().catch(() => ({}));
        failGeneration(j.detail || "Die Demo konnte nicht gestartet werden. Bitte später erneut versuchen.", myGen);
        return;
      }
      const data = await res.json();
      if (myGen !== genId.current) return;
      try {
        // AUTH-SICHERHEIT V2 · Phase 2: KEIN token mehr im localStorage.
        // Der Session-Cookie wurde vom Backend im Demo-Endpoint gesetzt
        // (siehe fetch mit credentials:"include" weiter oben).
        localStorage.setItem("owner_session", JSON.stringify({
          tenant_id: data.tenant_id,
          company_name: data.company_name,
          company_code: data.company_code,
          must_change_pin: !!data.must_change_pin,
        }));
        // D1.2: Zugangsdaten (Firmencode + temporäre PIN) bis nach dem Wizard sichern.
        // Das bestehende Zugangsdaten-Modal (DemoUpgrade) zeigt sie nach Abschluss.
        if (data.owner_pin) {
          localStorage.setItem("pending_demo_creds", JSON.stringify({
            demo_tenant_id: data.tenant_id,
            company_code: data.company_code,
            owner_pin: data.owner_pin,
          }));
        }
        localStorage.setItem(DEMO_LAST_KEY, String(Date.now()));
      } catch { /* localStorage optional */ }
      clearTimers();
      setDoneCount(DEMO_STEPS.length);
      setChecking(false);
      setTimeout(() => { window.location.href = "/unternehmer"; }, 700);
    } catch {
      failGeneration("Verbindungsproblem. Bitte versuchen Sie es später erneut.", myGen);
    }
  };

  // ROOT CUTOVER K1 (Juli 2026): Die alte Suchphase (Phase "search") existiert
  // nicht mehr. "Neue Vorschau erzeugen" führt per voller Dokument-Navigation
  // zurück zur statischen Landingpage – dem einzigen Landingpage-Einstieg.
  const resetToSearch = () => {
    genId.current++;
    clearTimers();
    window.location.replace("/landing/index.html");
  };

  // PLAN1.2 – EIN leichter, race-/cache-geschützter Vorschlags-Call. Niemals
  // blockierend, niemals Fehler im UI: schlägt er fehl, bleibt es beim
  // Einzelthema-Flow (keine Vorschläge). Nur aktiv, wenn das Feature-Flag an ist.
  // ADS-AUTOPREVIEW 01 Schritt 3: liefert die Einstufung zusätzlich zurück ({mode, suggestions}, sonst undefined).
  const fetchSuggestions = (t, key, myGen) => {
    if (!SUGGESTIONS_ENABLED) return;
    const cached = suggCache.current[key];
    if (cached) {
      if (myGen === genId.current) {
        setSuggestMode(cached.mode);
        setSuggestions(cached.suggestions);
        setSuggestReady(true);
      }
      return cached;
    }
    return fetch(`${API}/public/world-suggestions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: t }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data || myGen !== genId.current) return;
        const mode = data.mode || "single";
        const sugg = Array.isArray(data.suggestions) ? data.suggestions : [];
        suggCache.current[key] = { mode, suggestions: sugg };
        setSuggestMode(mode);
        setSuggestions(sugg);
        setSuggestReady(true);
        return suggCache.current[key];
      })
      .catch(() => { /* Vorschläge dürfen den Hauptflow nie stören */ });
  };

  const finishGeneration = (result, key, myGen) => {
    if (myGen !== genId.current) return;
    // BLOCK3A · Ergebnis wird registriert, ohne den laufenden Loader zu
    // überspringen. Der Wechsel zu "reveal" erfolgt AUSSCHLIESSLICH über den
    // Effect, der prüft, ob Abschnitt B vollständig durchlaufen ist. So werden
    // nie alle Haken auf einmal aktiviert und das Ergebnis erscheint nicht als
    // harter Sprung während der Animation.
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    cache.current[key] = result;
    setWorld(result);
    setResultReady(true);
  };

  const failGeneration = (msg, myGen) => {
    if (myGen !== undefined && myGen !== genId.current) return;
    clearTimers();
    setWorld(null);
    setChecking(false);
    setError(msg || "Bitte versuchen Sie es später erneut.");
    setPhase("error");
  };

  const poll = (jobId, key, myGen) => {
    pollTimer.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/public/world-preview/${jobId}`);
        if (myGen !== genId.current) return;
        if (!res.ok) { failGeneration("Die Vorschau ist abgelaufen. Bitte erneut versuchen.", myGen); return; }
        const data = await res.json();
        if (myGen !== genId.current) return;
        if (data.status === "done") {
          merkeErgebnis(jobId, data.result); // ADS-AUTOPREVIEW 01 Schritt 3: fertige Vorschau im Tab merken
          finishGeneration(data.result, key, myGen);
        } else if (data.status === "error") failGeneration(data.error, myGen);
      } catch {
        failGeneration("Verbindungsproblem. Bitte erneut versuchen.", myGen);
      }
    }, POLL_MS);
  };

  const generate = async (raw) => {
    const t = (raw || "").trim();
    if (!t) {
      const el = document.querySelector("[data-testid='lp2-search-input']");
      if (el) el.focus();
      return;
    }
    setTerm(t);
    const key = t.toLowerCase();
    const myGen = ++genId.current;

    if (cache.current[key]) {
      clearTimers();
      // BLOCK 3D · R3: Cache-Treffer führt ebenfalls direkt in den
      // Abo-Workflow. Kein `<WeltDokument>`-Mount, kein zweiter Renderer.
      const cached = cache.current[key];
      const token = cached?.preview_session_token;
      const worldId = cached?.world_id;
      if (token && worldId) {
        setError(null);
        try {
          // AUTH-SICHERHEIT V2 · Phase 2 · Preview-Ausnahme (siehe oben).
          localStorage.setItem(
            "owner_session",
            JSON.stringify({ preview_token: token, tenant_id: "public-preview" }),
          );
        } catch { /* localStorage optional */ }
        meldeStufe("vorschau_angezeigt", worldId); // MESS-10d, Betreiberfreigabe 03.11.2026
        window.location.href = `/unternehmer/welt/${worldId}`;
        return;
      }
      // Kein gecachtes Token → Cache verwerfen und regulär erneut generieren.
      delete cache.current[key];
    }
    if (recentPreviews().length >= MAX_PREVIEWS) {
      clearTimers();
      setWorld(null); setError(null); setPhase("limit"); scrollTop();
      return;
    }

    setPreviewsUsed(recordPreview());
    clearTimers();
    setError(null); setWorld(null);
    setSuggestions([]); setSuggestMode("single"); setSuggestReady(false);
    // BLOCK3A · Loader-Stage-Start: immer mit Abschnitt A, doneCount=0.
    // Der Step-Timer wird in einem eigenen Effect abhängig vom loaderStage
    // gestartet (siehe unten). `resultReady` wird zurückgesetzt, damit der
    // Reveal-Übergang erst nach echtem Backend-Ergebnis erfolgt.
    setLoaderStage("A"); setDoneCount(0); setChecking(false);
    setResultReady(false); setRevealPhase("hold-card");
    setShowEinmalkauf(false);
    setPhase("loading"); scrollTop();

    // ADS-AUTOPREVIEW 01 Schritt 3: Der Vorschlags-Call läuft jetzt VOR der Generierung (einstufen).
    try {
      const res = await fetch(`${API}/public/world-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mitHerkunft({ query: t })),
      });
      if (myGen !== genId.current) return;
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        failGeneration(j.detail || "Diese Eingabe konnte nicht verarbeitet werden.", myGen);
        return;
      }
      const data = await res.json();
      merkeVorschau(begriffRef.current, t, data.job_id); // ADS-AUTOPREVIEW 01 Schritt 3: begonnene Vorschau merken
      poll(data.job_id, key, myGen);
    } catch {
      failGeneration("Verbindungsproblem. Bitte erneut versuchen.", myGen);
    }
  };

  // ADS-AUTOPREVIEW 01 Schritt 3, Betreiberfreigabe 30.10.2026: vorhandene KI-Einstufung (world-suggestions)
  // VOR der Generierung. Eindeutige Begriffe starten ohne Klick; nur ein Betrieb ("business" mit Vorschlägen)
  // führt zur kurzen Themenauswahl. Fehler, Zeitüberschreitung oder Schalter aus → direkt generieren.
  // ADS-SUCHBEGRIFF-01, Betreiberfreigabe 01.11.2026: Füllwörter entfernen; bleibt kein Thema, kurze Frage statt Einstufung
  // und Erstellung. Der Begriff aus dem Ziel-Link bleibt Schlüssel der Wiederaufnahme (auch nach Eingabe in der Frage).
  const einstufen = async (eingabe, begriff = eingabe) => {
    begriffRef.current = begriff;
    const t = themaAusSuchbegriff(eingabe);
    if (!t) {
      ladeStandardwelten();
      setPhase("frage");
      return;
    }
    const myGen = ++genId.current;
    setTerm(t); setError(null); setPhase("loading"); scrollTop();
    const einstufung = await fetchSuggestions(t, t.toLowerCase(), myGen);
    if (myGen !== genId.current) return;
    if (einstufung?.mode === "business" && einstufung.suggestions.length > 0) {
      ladeStandardwelten();
      setPhase("auswahl");
      return;
    }
    generate(t);
  };

  // ADS-AUTOPREVIEW 01 Stufe 4.4B/4.5, Betreiberfreigabe 31.10.2026: vorhandener öffentlicher Katalog (alle, ungefiltert).
  // Ohne Liste bleiben Suchbegriff, KI-Themen und freies Thema bedienbar.
  const ladeStandardwelten = () => {
    fetch(`${API}/public/standardwelten`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setStandardwelten(Array.isArray(data?.welten) ? data.welten : []))
      .catch(() => { /* Katalog optional */ });
  };

  // ADS-AUTOPREVIEW 01 Stufe 4.4B/4.5: vorhandener Kopier-Endpunkt (eigene Kopie je Besucher, keine KI), danach
  // derselbe Ergebnisablauf wie die KI-Vorschau; im Tab gemerkt, damit Neuladen keine zweite Kopie anlegt.
  const oeffneStandardwelt = async (worldId) => {
    const myGen = ++genId.current;
    try {
      const res = await fetch(`${API}/public/standardwelt-vorschau`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mitHerkunft({ world_id: worldId })),
      });
      const data = await res.json().catch(() => ({}));
      if (myGen !== genId.current) return;
      if (!res.ok || !data.world_id || !data.preview_session_token) {
        failGeneration(data.detail || "Die Standardwelt konnte nicht geöffnet werden. Bitte erneut versuchen.", myGen);
        return;
      }
      merkeVorschau(begriffRef.current, worldId, `standardwelt:${worldId}`);
      merkeErgebnis(`standardwelt:${worldId}`, data);
      oeffneGemerkteWelt(data.world_id, data.preview_session_token);
    } catch {
      failGeneration("Verbindungsproblem. Bitte erneut versuchen.", myGen);
    }
  };

  // ADS-AUTOPREVIEW 01 Schritt 3: fertige Vorschau des Tabs öffnen; replace, damit „Zurück“ nicht erneut hierher führt.
  const oeffneGemerkteWelt = (worldId, token) => {
    try {
      localStorage.setItem(
        "owner_session",
        JSON.stringify({ preview_token: token, tenant_id: "public-preview" }),
      );
    } catch { /* localStorage optional */ }
    meldeStufe("vorschau_angezeigt", worldId); // MESS-10d, Betreiberfreigabe 03.11.2026
    window.location.replace(`/unternehmer/welt/${worldId}`);
  };

  // ADS-AUTOPREVIEW 01 Schritt 3: Neuladen desselben Links nutzt die begonnene oder fertige Vorschau des Tabs
  // (keine neue Einstufung, keine neue Generierung). Nicht mehr verwendbar → regulär neu einstufen.
  const wiederaufnehmen = async (g) => {
    begriffRef.current = g.begriff;
    if (g.world_id && g.preview_session_token) {
      oeffneGemerkteWelt(g.world_id, g.preview_session_token);
      return;
    }
    const myGen = ++genId.current;
    setTerm(g.thema); setPhase("loading");
    let data = null;
    try {
      const res = await fetch(`${API}/public/world-preview/${g.job_id}`);
      data = res.ok ? await res.json() : null;
    } catch {
      data = null;
    }
    if (myGen !== genId.current) return;
    if (data?.status === "running") {
      poll(g.job_id, g.thema.toLowerCase(), myGen);
      return;
    }
    if (data?.status === "done" && data.result?.world_id && data.result?.preview_session_token) {
      merkeErgebnis(g.job_id, data.result);
      oeffneGemerkteWelt(data.result.world_id, data.result.preview_session_token);
      return;
    }
    vergissVorschau();
    einstufen(g.begriff);
  };

  // Q4.3 – Landing-Deep-Link: Wird /lp3 mit "?q=<Suchbegriff>" aufgerufen (aus
  // der neuen statischen Landingpage), starten wir GENAU EINMAL den bestehenden
  // Preview-Flow. Ohne "?q" oder mit leerem Wert bleibt das heutige Verhalten
  // (Phase "search") unverändert. Wiederverwendung von `generate()` — KEINE
  // Duplizierung der Preview-Logik. Guard über useRef schützt gegen React
  // Strict Mode, doppelten Effect, Re-Renders und spätere Re-Mounts.
  const autoStartRef = useRef(false);
  useEffect(() => {
    erfasseHerkunft(window.location.search); // MESS-06, Betreiberfreigabe 27.10.2026: Herkunft vor der q-Prüfung erfassen
    if (autoStartRef.current) return;
    let raw = null;
    let utmTerm = null; // ADS-AUTOPREVIEW 01 Schritt 3, Betreiberfreigabe 30.10.2026: utm_term nur als Rückfall
    try {
      const params = new URLSearchParams(window.location.search);
      raw = params.get("q");
      utmTerm = params.get("utm_term");
    } catch {
      raw = null;
    }
    const value = generierungsbegriff(raw, utmTerm);
    if (!value) {
      // ROOT CUTOVER K1: /lp3 ohne gültiges "q" → statische Landingpage.
      // Die alte Suchphase existiert nicht mehr; kein Deep-Link darf den
      // alten React-Startscreen erreichen.
      window.location.replace("/landing/index.html");
      return;
    }
    autoStartRef.current = true;
    const gemerkt = gemerkteVorschau(value); // ADS-AUTOPREVIEW 01 Schritt 3: Wiederaufnahme vor Einstufung
    if (gemerkt) wiederaufnehmen(gemerkt);
    else einstufen(value);
    // Bewusst leere Deps: der Auto-Start soll ausschliesslich beim initialen
    // Seiten-Mount erfolgen, nicht bei State-Aenderungen oder erneutem Render.
  }, []);

  return (
    <div className="lp2-root" id="top" data-testid="lp3-flow-page">
      {/* BLOCK3 · Fokusmodus während des LP3-Conversion-Flows:
          Ab dem Klick auf "Vorschlag prüfen" (phase="loading") bis einschließlich
          ConversionBlock/Pricing (phase="done") sind Header und Footer ausgeblendet,
          damit der Nutzer den geführten Funnel nicht durch Navigation verlässt.
          Sichtbar bleibt Header/Footer nur außerhalb des Flows:
          search · demo · demo-limit · error · limit.
          Sobald der Nutzer den Flow verlässt (z.B. "Neue Vorschau erzeugen"),
          erscheinen Header und Footer unverändert wieder. */}
      {phase !== "loading" && phase !== "reveal" && phase !== "world"
        && phase !== "assign" && phase !== "done" && (
        <LandingHeader onLogin={goTest} onTest={startDemo} />
      )}

      <main>
        {/* ROOT CUTOVER K1: Die alte Startseite (Phase "search" mit
            WeltSucheHero/WowFlow/PricingSection) wurde entfernt. Der einzige
            Landingpage-Einstieg ist die statische Seite unter "/"; diese
            Komponente dient nur noch dem LP3-Generierungsflow (?q=…). */}
        {phase === "loading" && (
          <ChrisLp3Waiting
            term={term}
            ready={resultReady && !!world && !!world.preview_session_token && !!world.world_id}
            onHandoff={handoffToWelt}
          />
        )}

        {phase === "auswahl" && (
          <BegriffAuswahl
            begriff={term}
            vorschlaege={suggestions}
            standardwelten={standardwelten}
            onWahl={generate}
            onStandardwelt={oeffneStandardwelt}
          />
        )}

        {/* ADS-SUCHBEGRIFF-01, Betreiberfreigabe 01.11.2026: kurze Frage ohne Thema; Eingabe läuft durch die Einstufung. */}
        {phase === "frage" && (
          <ThemaFrage
            begriff={begriffRef.current} // ADS-ABSICHT-01, Betreiberfreigabe 01.11.2026
            standardwelten={standardwelten}
            onWahl={(eingabe) => einstufen(eingabe, begriffRef.current)}
            onStandardwelt={oeffneStandardwelt}
          />
        )}

        {phase === "demo" && (
          <section className="lp4-stage" data-testid="lp-demo-loader">
            <div className="lp4-stage-term" data-testid="lp-demo-stage-term">Ihre Demo wird vorbereitet …</div>
            <KiArbeitskarte steps={DEMO_STEPS} doneCount={doneCount} checking={checking} />
            <p className="lp4-stage-hint">Kein Login, keine Registrierung – Sie sind gleich im fertigen Betrieb.</p>
          </section>
        )}

        {phase === "demo-limit" && (
          <section className="lp4-stage" data-testid="lp-demo-limit">
            <div className="lp4-msg">
              <ShieldAlert size={34} color="#94a3b8" />
              <h2>Sie haben heute bereits eine Demo gestartet.</h2>
              <p>Pro Tag ist eine kostenlose Demo möglich. Melden Sie sich an, um dort weiterzumachen, oder versuchen Sie es morgen erneut.</p>
              <button className="btn btn-primary btn-lg" data-testid="lp-demo-limit-login" onClick={goTest}>
                <Rocket size={16} /> Zur Anmeldung
              </button>
            </div>
          </section>
        )}

        {phase === "error" && (
          <section className="lp4-stage" data-testid="lp3-error">
            <div className="lp4-msg">
              <ShieldAlert size={34} color="#94a3b8" />
              <h2>Vorschau konnte nicht erstellt werden</h2>
              <p>{error}</p>
              <button className="btn btn-primary btn-lg" data-testid="lp3-error-retry" onClick={resetToSearch}>
                <RefreshCw size={16} /> Neue Vorschau erzeugen
              </button>
            </div>
          </section>
        )}

        {phase === "limit" && (
          <section className="lp4-stage" data-testid="lp2-limit">
            <div className="lp4-msg">
              <ShieldAlert size={34} color="#94a3b8" />
              <h2>Sie haben Ihre {MAX_PREVIEWS} kostenlosen Vorschauen für heute genutzt.</h2>
              <p>Im kostenlosen Test erzeugen Sie unbegrenzt Welten und speichern Ihre Nachweise revisionssicher.</p>
              <button className="btn btn-primary btn-lg" data-testid="lp2-limit-cta" onClick={startDemo}>
                <Rocket size={16} /> Demo testen
              </button>
            </div>
          </section>
        )}

        {phase === "reveal" && revealPhase !== "hold-card" && revealPhase !== "fade-card" && (
          <section className="lp4-stage" data-testid="lp3-reveal">
            <style>{`
              @keyframes lp3-headline-fadein {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
              @keyframes lp3-subline-fadein {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
              [data-testid="lp3-reveal-inner"][data-anim="in"] [data-testid="lp3-reveal-headline"] {
                animation: lp3-headline-fadein 700ms ease-out both;
              }
              [data-testid="lp3-reveal-inner"][data-anim="in"] [data-testid="lp3-reveal-subline"] {
                animation: lp3-subline-fadein 600ms ease-out 350ms both;
              }
            `}</style>
            <div
              className="lp4-msg"
              data-testid="lp3-reveal-inner"
              data-anim={revealPhase === "pause" ? "out" : "in"}
              style={{
                textAlign: "center",
                opacity: revealPhase === "fade-out" ? 0 : 1,
                transition: revealPhase === "fade-out" ? "opacity 600ms ease" : undefined,
              }}
            >
              <h2
                data-testid="lp3-reveal-headline"
                style={{
                  fontSize: "clamp(28px, 4.6vw, 44px)",
                  lineHeight: 1.15,
                  letterSpacing: "-0.01em",
                  color: "#0f172a",
                  margin: 0,
                  whiteSpace: "pre-line",
                }}
              >
                {"IHRE PRÜFSICHERHEIT\nBEGINNT JETZT."}
              </h2>
              <p
                data-testid="lp3-reveal-subline"
                style={{
                  marginTop: 14,
                  fontSize: "clamp(15px, 1.4vw, 17px)",
                  lineHeight: 1.5,
                  color: "#475569",
                  maxWidth: 560,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Die Grundlage Ihrer Prüfsicherheit wurde erfolgreich erstellt.
              </p>
            </div>
          </section>
        )}

        {phase === "world" && world && (
          <>
            <style>{`
              @keyframes lp3-world-fadein {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
              [data-testid="lp3-world-wrapper"] {
                animation: lp3-world-fadein 600ms ease-out both;
              }
            `}</style>
            <div data-testid="lp3-world-wrapper">
              <WeltDokument world={world} onAssign={() => { setPhase("assign"); scrollTop(); }} onNew={resetToSearch} />
              {showEinmalkauf && <EinmalkaufPanel content={world} />}
              {suggestMode === "business" && suggestions.length > 0 && (
                <WeltVorschlaege suggestions={suggestions} />
              )}
            </div>
          </>
        )}

        {phase === "assign" && (
          <MitarbeiterZuweisungDemo onDone={() => { setPhase("done"); scrollTop(); }} />
        )}

        {phase === "done" && (
          <>
            <ConversionBlock topic={term} onRetry={resetToSearch} onTest={startDemo} />
            <PricingSection onTest={startDemo} />
          </>
        )}
      </main>

      {/* BLOCK3 · Fokusmodus: Footer nur außerhalb des LP3-Conversion-Flows.
          Kein Impressum/Datenschutz/AGB/AVV/Mitarbeiter-Login/Copyright, solange
          der Nutzer sich im geführten Funnel (loading → reveal → world → assign → done)
          befindet. */}
      {phase !== "loading" && phase !== "reveal" && phase !== "world"
        && phase !== "assign" && phase !== "done" && (
        <footer data-testid="lp2-footer">
          <div className="wrap ft">
            <span className="lg">
              <img
                src="/brand/clartext-logo-full-light.svg"
                alt="CLartext"
                style={{ height: 36, width: "auto", maxWidth: "100%", display: "block" }}
                data-testid="lp2-footer-logo"
              />
            </span>
            <nav className="ft-legal" data-testid="lp2-footer-legal">
              <Link to="/impressum" data-testid="legal-link-impressum">Impressum</Link>
              <Link to="/datenschutz" data-testid="legal-link-datenschutz">Datenschutz</Link>
              <Link to="/agb" data-testid="legal-link-agb">AGB</Link>
              {/* ABO-RECHT-02, Betreiberfreigabe 27.10.2026 — Linktext wie landing/index.html. */}
              <Link to="/avv" data-testid="legal-link-avv">Auftragsverarbeitung</Link>
              <Link to="/monteur/login" data-testid="footer-link-monteur-login">Mitarbeiter-Login</Link>
            </nav>
            <small>© 2026 CLartext · Landingpage</small>
          </div>
        </footer>
      )}
    </div>
  );
}
